import { useEffect } from 'react';
import { Chip, CircularProgress } from '@mui/material';

import Iconify from 'src/components/iconify';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';

import CasPendingEmailsDialog from './CasPendingEmailsDialog';
import { usePendingEmails } from './usePendingEmails';

/**
 * Floating indicator for the current user's in-flight / failed emails.
 *
 * Fixed to the bottom-left rather than living in CasHeader because the header AppBar sits at
 * zIndex appBar + 1 (1101), so it is painted under any open dialog - and sends are started from
 * a dialog, which is exactly when this needs to stay visible.
 */
export default function CasPendingEmailsPill() {
  const { rows, count, errorCount, sendingCount, hasError } = usePendingEmails();
  const dialog = useCasBoolean();
  const { setFalse: closeDialog } = dialog;

  // Reset the open flag once nothing is pending. The `count === 0` early return below only makes
  // this component render null - it stays mounted, so without this the flag stays true and the
  // dialog springs open by itself the moment the next email is sent.
  useEffect(() => {
    if (count === 0) closeDialog();
  }, [count, closeDialog]);

  if (count === 0) return null;

  // Errors win over in-flight sends: a failure is the thing the user has to act on, and it
  // persists until dismissed, whereas "Sending" clears itself within seconds.
  const label = (() => {
    if (hasError) return errorCount > 1 ? `Error sending (${errorCount})` : 'Error sending';
    return sendingCount > 1 ? `Sending ${sendingCount} emails` : 'Sending 1 email';
  })();

  return (
    <>
      <Chip
        icon={
          hasError ? (
            <Iconify width="16px" height="16px" icon="eva:alert-triangle-fill" />
          ) : (
            <CircularProgress size={14} color="inherit" />
          )
        }
        label={label}
        // "soft" (a theme variant, see theme/overrides/components/chip.tsx) rather than the
        // default filled: a filled color="default" chip is styled dark-background/white-text, so
        // giving it a light background leaves white text on white. Soft supplies both halves.
        variant="soft"
        color={hasError ? 'error' : 'default'}
        onClick={dialog.setTrue}
        sx={{
          position: 'fixed',
          bottom: 24,
          /*
            Bottom LEFT, not right: a send is started from a dialog, and on the right this
            sat squarely over that dialog's action buttons. Indented past the browser's
            open-in-new-tab control, which also renders in this corner - hence a left offset
            rather than the 24 used on the other axis.
          */
          left: 72,
          // Above dialogs (modal, 1300) so it stays visible while composing, below snackbars
          // (1400) so it never fights a toast.
          zIndex: (theme) => theme.zIndex.modal + 50,
          boxShadow: (theme) => theme.customShadows?.z8,
          // Safe to set a background here only because "soft" leaves the text at text.primary;
          // the filled default would still force white text onto it. Errors keep the soft red so
          // they don't read as just another in-progress send.
          ...(hasError ? {} : { bgcolor: 'warning.lighter' }),
          '& .MuiChip-icon': { ml: 1 },
        }}
      />

      {dialog.value && <CasPendingEmailsDialog rows={rows} onClose={dialog.setFalse} />}
    </>
  );
}
